import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { Alert, Button, Stack, Text } from '@mantine/core';
import { AutomationSchemaRef, SchemaValidationMode } from '@/types/automationFields.ts';
import type { TransformInputContract } from '@/types/automationSteps.ts';

type Props = {
  bindingNames: string[];
  contracts: TransformInputContract[];
  schemaOptions: { value: string; label: string }[];
  onChange: (contracts: TransformInputContract[]) => void;
  title?: string;
};

const isAdvancedContract = (contract: TransformInputContract) =>
  (contract.accepted_schema_refs?.length ?? 0) > 1 ||
  (contract.accepted_dataset_types?.length ?? 0) > 0 ||
  (contract.allowed_source_types?.length ?? 0) > 0 ||
  contract.required === false ||
  contract.allow_multiple === true ||
  (contract.validation_mode && contract.validation_mode !== SchemaValidationMode.Error);

const contractSummary = (contract: TransformInputContract) => {
  const parts: string[] = [];
  const schemas = contract.accepted_schema_refs ?? [];
  if (schemas.length) {
    parts.push(
      `Schemas: ${schemas.map((schema) => `${schema.schema_id} v${schema.version}`).join(', ')}`,
    );
  } else {
    parts.push('Schemas: any');
  }
  if ((contract.accepted_dataset_types?.length ?? 0) > 0) {
    parts.push(`Dataset types: ${contract.accepted_dataset_types.join(', ')}`);
  }
  if ((contract.allowed_source_types?.length ?? 0) > 0) {
    parts.push(`Sources: ${contract.allowed_source_types.join(', ')}`);
  }
  parts.push(`Cardinality: ${contract.allow_multiple ? 'multiple' : 'single'}`);
  parts.push(`Required: ${contract.required === false ? 'no' : 'yes'}`);
  parts.push(`Runtime mode: ${contract.validation_mode ?? SchemaValidationMode.Error}`);
  return parts.join(' · ');
};

const CodeInputContractFields = ({
  bindingNames,
  contracts,
  schemaOptions,
  onChange,
  title = 'Code input contracts',
}: Props) => {
  const unmatchedContracts = contracts.filter(
    (contract) => !bindingNames.includes(contract.binding_name),
  );
  const hasUnresolvedOrphan = unmatchedContracts.length > 0;

  const updateSchema = (bindingName: string, schemaValue: string | null) => {
    const existing = contracts.find((contract) => contract.binding_name === bindingName);
    if (existing && isAdvancedContract(existing)) {
      return;
    }
    const remaining = contracts.filter((contract) => contract.binding_name !== bindingName);
    if (!schemaValue) {
      onChange(remaining);
      return;
    }
    const schema = JSON.parse(schemaValue) as AutomationSchemaRef;
    onChange([
      ...remaining,
      {
        binding_name: bindingName,
        accepted_schema_refs: [schema],
        accepted_dataset_types: [],
        allowed_source_types: [],
        required: true,
        allow_multiple: false,
        validation_mode: SchemaValidationMode.Error,
      },
    ]);
  };

  return (
    <Stack gap="xs">
      <Alert color="blue" title={title}>
        Simple editing supports one required schema with error enforcement. Multi-schema or
        otherwise advanced contracts are preserved and shown below, but must be changed through an
        advanced authoring surface. A missing contract is a review warning that may require
        follow-up; an incompatible declared contract blocks publishing.
      </Alert>
      {hasUnresolvedOrphan && (
        <Alert color="yellow" title="Contract needs attention">
          <Stack gap="xs">
            <Text size="sm">
              One or more contracts reference inputs that no longer exist. Resolve these explicitly before
              editing another input so the editor does not submit an invalid hidden contract.
            </Text>
            {unmatchedContracts.map((contract) => {
              const advanced = isAdvancedContract(contract);
              return (
                <Stack key={contract.binding_name} gap={2}>
                  <Text size="xs" c={advanced ? 'yellow' : 'dimmed'}>
                    {contract.binding_name}: {contractSummary(contract)}
                  </Text>
                  {advanced ? (
                    <Text size="xs">
                      This advanced contract must be removed or reassigned through an advanced authoring surface.
                    </Text>
                  ) : (
                    <Button
                      size="xs"
                      variant="subtle"
                      w="fit-content"
                      onClick={() =>
                        onChange(
                          contracts.filter(
                            (item) => item.binding_name !== contract.binding_name,
                          ),
                        )
                      }
                    >
                      Remove stale contract
                    </Button>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Alert>
      )}
      {bindingNames.map((bindingName) => {
        const contract = contracts.find((item) => item.binding_name === bindingName);
        const advanced = !!contract && isAdvancedContract(contract);
        const selected = contract?.accepted_schema_refs?.[0];
        return (
          <Stack key={bindingName} gap={4}>
            <StyledSelect
              label={`Required schema for ${bindingName}`}
              placeholder={contract ? 'No schema requirement' : 'No contract declared'}
              data={schemaOptions}
              value={selected ? JSON.stringify(selected) : null}
              onChange={(value) => updateSchema(bindingName, value)}
              clearable
              disabled={advanced || hasUnresolvedOrphan}
              description={
                hasUnresolvedOrphan
                  ? 'Resolve the stale contract above before editing this input.'
                  : advanced
                  ? 'Advanced contract preserved. Simple schema editing is disabled to avoid narrowing it.'
                  : 'Leave blank only when this code can safely accept an untyped input. Risky publish paths require review acknowledgement.'
              }
            />
            {contract && (
              <Text size="xs" c={advanced ? 'yellow' : 'dimmed'}>
                {contractSummary(contract)}
              </Text>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default CodeInputContractFields;
